import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const CART_KEY = "vedaheal_cart";
const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.product_id === product.product_id);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
                return copy;
            }
            return [
                ...prev,
                {
                    product_id: product.product_id,
                    name: product.name,
                    price: product.price,
                    image_url: product.image_url,
                    quantity: qty,
                },
            ];
        });
    }, []);

    const updateQty = useCallback((pid, qty) => {
        setItems((prev) =>
            prev
                .map((p) => (p.product_id === pid ? { ...p, quantity: qty } : p))
                .filter((p) => p.quantity > 0),
        );
    }, []);

    const removeItem = useCallback((pid) => {
        setItems((prev) => prev.filter((p) => p.product_id !== pid));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const totalQty = useMemo(
        () => items.reduce((s, i) => s + i.quantity, 0),
        [items],
    );
    const totalPrice = useMemo(
        () => items.reduce((s, i) => s + i.quantity * i.price, 0),
        [items],
    );

    const value = useMemo(
        () => ({ items, addItem, updateQty, removeItem, clear, totalQty, totalPrice }),
        [items, addItem, updateQty, removeItem, clear, totalQty, totalPrice],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
}
